/**
 * GenieSetupImmersive — the full-bleed, brand-themed setup presentation that takes
 * over the whole screen when an owner lands on an unfinished genie. Same five-step
 * data as the in-page companion (via useGenieSetupState), reskinned as a two-pane
 * presentation tinted to the genie's OWN branding (logo, primary/secondary colours).
 *
 * Layout deliberately rhymes with the live workshop (ImmersiveSetupPage): a branded
 * HERO stage on the left, a focused white setup PANEL on the right.
 *
 * Cohesion rule: steps NEVER eject to the settings card stack. Knowledge, voice
 * and brand open their real editors IN PLACE (drawers layered over this
 * presentation); only "take it for a spin" opens the live test-and-tune workshop,
 * since a real-time call can't happen in a drawer. "View all features" is the only
 * deliberate exit to the full card stack. There's no "share" step — getting the
 * genie out there is the Activate panel's job, not another checklist item.
 *
 * z-index note: this overlay sits at z-[60] — above app chrome (sidebar z-40, nav
 * z-50) but below the drawers/sheets (z-[70]) so the in-place editors layer on top.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Undo2, X } from "lucide-react";
import { KnowledgeDrawer } from "@/components/agent/drawers/KnowledgeDrawer";
import { BrandingModal } from "@/components/branding";
import PhoneNumberModal from "@/components/phone/PhoneNumberModal";
import VoiceModal from "@/components/agent/modals/configuration/VoiceModal";
import { useGenieSetupState } from "@/hooks/agent/useGenieSetupState";
import { genieSkippedKey, genieTestedKey, lsGet, lsSet, readSkippedSteps } from "@/utils/agent/genieSetupStorage";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { getGenieBrandTheme, withAlpha } from "@/utils/branding/genieBrandTheme";
import { Agent } from "@/types/agent/agent";
import { GenieSetupStep, GenieSetupStepId } from "@/types/agent/genie-setup";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface GenieSetupImmersiveProps {
  genie: Agent;
  /** Exit the presentation and show the full settings cards. */
  onViewAll: () => void;
}

/** Which in-place editor is open over the presentation (null = none). */
type EditorId = "knowledge" | "voice" | "brand" | "phone" | null;

const CTA_LABEL: Record<GenieSetupStepId, string> = {
  knowledge: "Add knowledge",
  voice: "Choose a voice",
  brand: "Add your brand",
  phone: "Add a number",
  test: "Take it live",
};

export const GenieSetupImmersive = ({ genie, onViewAll }: GenieSetupImmersiveProps) => {
  const navigate = useNavigate();

  const testedKey = genieTestedKey(genie.id);
  const [tested, setTested] = useState(() => lsGet(testedKey) === "1");
  const [editor, setEditor] = useState<EditorId>(null);

  // Not every genie wants a phone number, and being nagged forever about a step
  // you have decided against is not setup, it is a to-do list that never ends.
  // Per-device on purpose: there is nowhere on the agent record we own to write
  // this, so skipping on a laptop will not follow you to a phone.
  const skippedKey = genieSkippedKey(genie.id);
  const [skipped, setSkipped] = useState<GenieSetupStepId[]>(() => readSkippedSteps(genie.id));

  const toggleSkip = useCallback(
    (id: GenieSetupStepId) => {
      setSkipped((current) => {
        const next = current.includes(id) ? current.filter((s) => s !== id) : [...current, id];
        lsSet(skippedKey, JSON.stringify(next));
        return next;
      });
    },
    [skippedKey],
  );

  const { steps, doneCount, skippedCount, totalCount, progress, isComplete } = useGenieSetupState(
    genie,
    { tested, skipped },
  );

  // ── Brand the world to this genie ──
  const { primary, secondary, gradient: brandGradient } = getGenieBrandTheme(genie);
  const avatarUrl = resolveGenieAvatarUrl(genie);

  const remaining = totalCount - doneCount - skippedCount;
  const currentIndex = steps.findIndex((s) => s.status !== "done" && s.status !== "skipped");

  const launchWorkshop = () => {
    setTested(true);
    lsSet(testedKey, "1");
    navigate(`/genies/${genie.id}/immersive-setup`);
  };

  const handleStep = (step: GenieSetupStep) => {
    switch (step.id) {
      case "test":
        // Live testing needs the real-time call workshop — the one deliberate exit.
        launchWorkshop();
        break;
      case "voice":
        setEditor("voice");
        break;
      case "knowledge":
        setEditor("knowledge");
        break;
      case "brand":
        setEditor("brand");
        break;
      case "phone":
        setEditor("phone");
        break;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden"
      style={{
        backgroundColor: "#FBFCFE",
        backgroundImage: `radial-gradient(60% 50% at 10% -6%, ${withAlpha(primary, "26")}, transparent 60%), radial-gradient(55% 45% at -4% 108%, ${withAlpha(secondary, "1f")}, transparent 60%)`,
      }}
    >
      {/* Floating back link — always reachable, sits above both panes */}
      <button
        type="button"
        onClick={() => navigate("/genie-hub/my-genies")}
        className="absolute top-4 left-4 sm:left-5 z-30 h-9 pl-2.5 pr-3.5 rounded-full bg-white/80 backdrop-blur border border-black/[0.06] shadow-sm text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-white transition-colors flex items-center gap-1"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        My Genies
      </button>

      {/* Floating escape — always reachable, sits above both panes */}
      <div className="absolute top-4 right-4 sm:right-5 z-30 flex items-center gap-1.5">
        <button
          type="button"
          onClick={onViewAll}
          className="h-9 pl-3.5 pr-3 rounded-full bg-white/80 backdrop-blur border border-black/[0.06] shadow-sm text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-white transition-colors flex items-center gap-1"
        >
          View all features
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onViewAll}
          aria-label="Close setup"
          className="w-9 h-9 rounded-full bg-white/80 backdrop-blur border border-black/[0.06] shadow-sm flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ════════ LEFT — branded presentation stage ════════ */}
      <section className="relative lg:flex-1 lg:min-h-0 flex flex-col items-center justify-center text-center px-6 pt-20 pb-10 lg:py-12">
        <div className="w-full max-w-md flex flex-col items-center">
          {/* Avatar — the genie's own logo, brand-glowed */}
          <div className="relative mb-6">
            <div
              className="absolute -inset-4 rounded-full blur-2xl opacity-40"
              style={{ background: brandGradient }}
            />
            <div
              className="relative w-24 h-24 lg:w-28 lg:h-28 rounded-full flex items-center justify-center overflow-hidden"
              style={{
                background: avatarUrl ? "#FFFFFF" : brandGradient,
                boxShadow: `0 24px 48px -16px ${withAlpha(primary, "80")}`,
              }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt={genie.name} className="w-[78%] h-[78%] object-contain" />
              ) : (
                <GenieIcon name="genie-lamp" size={48} color="#FFFFFF" />
              )}
            </div>
          </div>

          <span
            className="text-[11px] font-bold tracking-[0.18em] uppercase"
            style={{ color: primary }}
          >
            {isComplete ? "Ready to work" : "Setting up"}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-[#222B45] tracking-tight mt-2 leading-[1.1]">
            {isComplete ? `${genie.name} is ready` : `${genie.name} is taking shape`}
          </h1>
          <p className="text-base text-gray-500 mt-3 max-w-sm leading-relaxed">
            {isComplete
              ? "Every essential is in place. Take it for a spin, or fine-tune anything you like."
              : `Already drafted from your details — ${remaining} ${remaining === 1 ? "thing" : "things"} left and it's ready for customers.`}
          </p>

          {/* Progress */}
          <div className="w-full max-w-sm mt-7 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-black/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-700 ease-out"
                style={{ width: `${Math.round(progress * 100)}%`, background: brandGradient }}
              />
            </div>
            <span className="text-xs font-bold text-[#222B45] flex-shrink-0">
              {doneCount}/{totalCount}
            </span>
          </div>

          {/* Primary action — the one big move */}
          {genie.type !== "listener" && (
            <button
              type="button"
              onClick={launchWorkshop}
              className="mt-7 h-12 px-7 rounded-2xl flex items-center justify-center gap-2 text-[15px] font-semibold text-white shadow-lg hover:opacity-95 hover:scale-[1.02] transition-all"
              style={{ background: brandGradient, boxShadow: `0 14px 30px -10px ${withAlpha(primary, "99")}` }}
            >
              <GenieIcon name="play" size={18} color="#FFFFFF" />
              Test &amp; tune live
            </button>
          )}

          <p className="mt-4 text-xs text-gray-400 max-w-xs leading-relaxed">
            We've set up everything else — finish what's left, right here.
          </p>
        </div>
      </section>

      {/* ════════ RIGHT — focused setup panel (steps open editors in place) ════════ */}
      <aside className="w-full lg:w-[520px] xl:w-[600px] 2xl:w-[680px] flex-shrink-0 bg-white lg:h-full lg:border-l border-black/[0.06] lg:shadow-[-18px_0_40px_-24px_rgba(15,23,42,0.25)] flex flex-col lg:min-h-0">
        {/* Panel header */}
        <div className="flex items-center gap-3 px-6 lg:px-8 pt-6 pb-5 border-b border-[#F1F3F7] flex-shrink-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: brandGradient }}
          >
            <GenieIcon name="clipboard-check" size={18} color="#FFFFFF" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-[#222B45] leading-tight">
              {isComplete ? "Everything's set" : "Finish setting up"}
            </h2>
            <p className="text-[13px] text-gray-500 leading-tight mt-0.5">
              {isComplete ? "Tweak anything whenever you like" : `${remaining} ${remaining === 1 ? "step" : "steps"} to go`}
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="flex-1 lg:overflow-y-auto px-6 lg:px-8 py-4">
          <ol className="relative">
            {/* connector line */}
            <span className="absolute left-[19px] top-5 bottom-5 w-px bg-gray-200" aria-hidden />

            {steps.map((step, i) => {
              const done = step.status === "done";
              const isSkipped = step.status === "skipped";
              const isCurrent = i === currentIndex;
              return (
                <li key={step.id} className="relative pl-[52px] pb-2.5 last:pb-0">
                  {/* node */}
                  <span
                    className="absolute left-0 top-1 w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold z-10 transition-all"
                    style={
                      done
                        ? { background: brandGradient, color: "#FFFFFF", boxShadow: `0 6px 14px -6px ${withAlpha(primary, "99")}` }
                        : isSkipped
                          ? { background: "#F7F8FB", color: "#9AA3B2", border: "2px solid #E5EAF0" }
                          : isCurrent
                            ? { background: "#FFFFFF", color: primary, border: `2px solid ${primary}`, boxShadow: `0 0 0 4px ${withAlpha(primary, "1f")}` }
                            : { background: "#FFFFFF", color: "#9AA3B2", border: "2px solid #E5EAF0" }
                    }
                  >
                    {done ? <Check className="w-4 h-4" strokeWidth={3} /> : String(i + 1).padStart(2, "0")}
                  </span>

                  {/* card */}
                  <button
                    type="button"
                    onClick={() => handleStep(step)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white text-left transition-all hover:shadow-md"
                    style={{
                      border: isCurrent ? `1.5px solid ${primary}` : "1px solid #EAEEF3",
                      boxShadow: isCurrent
                        ? `0 10px 22px -12px ${withAlpha(primary, "66")}`
                        : "0 1px 2px rgba(16,24,40,0.04)",
                      opacity: isSkipped ? 0.55 : 1,
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: withAlpha(primary, "14") }}
                    >
                      <GenieIcon name={step.genieIcon} size={17} color={primary} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[14px] font-bold text-[#222B45] block truncate">
                        {step.label}
                      </span>
                      <span className="text-[12px] text-gray-500 block truncate">
                        {isSkipped ? "You said this one is not for you" : step.hint}
                      </span>
                    </div>
                    {done ? (
                      <span
                        className="px-2.5 h-6 rounded-full flex items-center gap-1 text-[11px] font-bold flex-shrink-0"
                        style={{ backgroundColor: withAlpha(primary, "14"), color: primary }}
                      >
                        <Check className="w-3 h-3" strokeWidth={3} />
                        Done
                      </span>
                    ) : isSkipped ? (
                      <span className="px-2.5 h-6 rounded-full flex items-center text-[11px] font-bold flex-shrink-0 bg-[#F3F5F9] text-[#7A8596]">
                        Skipped
                      </span>
                    ) : (
                      <span
                        className="px-3 h-8 rounded-lg flex items-center gap-1 text-[12px] font-bold flex-shrink-0 transition-colors"
                        style={
                          isCurrent
                            ? { background: brandGradient, color: "#FFFFFF" }
                            : { backgroundColor: "#F3F5F9", color: "#3D4557" }
                        }
                      >
                        {CTA_LABEL[step.id]}
                        <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </button>

                  {/* Skip control. Outside the card button on purpose — a button
                      inside a button is invalid, and this is a quiet secondary
                      action, not a peer of the CTA. Never shown on a done step:
                      there is nothing left to wave off. */}
                  {!done && (
                    <div className="flex justify-end pt-1 pr-1">
                      <button
                        type="button"
                        onClick={() => toggleSkip(step.id)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#9AA3B2] hover:text-[#3D4557] transition-colors"
                      >
                        {isSkipped ? (
                          <>
                            <Undo2 className="w-3 h-3" />
                            Add it back
                          </>
                        ) : (
                          "Not for me"
                        )}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* ════════ Activate — the hero finish line ════════
            Getting the Genie in front of people is the whole point, so this is a
            key, full-presence block — not a footnote. The activation pack loops
            straight back here, so we never leave the Genie. */}
        <div className="px-5 lg:px-7 pb-5 pt-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate(`/genie-hub/activation/genie/${genie.id}`)}
            className="group relative w-full overflow-hidden rounded-3xl px-6 py-6 lg:px-7 lg:py-7 text-left text-white transition-transform hover:scale-[1.01]"
            style={{ background: brandGradient, boxShadow: `0 26px 52px -20px ${withAlpha(primary, "aa")}` }}
          >
            {/* depth sheen */}
            <span
              className="pointer-events-none absolute -top-1/3 -right-1/4 w-2/3 h-[170%] rotate-12 bg-white/10 blur-2xl"
              aria-hidden
            />
            {/* twinkles */}
            <span className="pointer-events-none absolute top-5 right-6 text-white animate-twinkle">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
              </svg>
            </span>
            <span
              className="pointer-events-none absolute top-12 right-14 text-white/70 animate-twinkle"
              style={{ animationDelay: "0.9s" }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
              </svg>
            </span>

            {/* Lead */}
            <div className="relative flex items-start gap-4">
              <span className="w-14 h-14 rounded-2xl bg-white/20 ring-1 ring-white/30 flex items-center justify-center flex-shrink-0">
                <GenieIcon name="qr-code" size={30} color="#FFFFFF" />
              </span>
              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-white/80 block">
                  {isComplete ? "Ready to launch" : "Get it out there"}
                </span>
                <h3 className="text-xl lg:text-2xl font-bold leading-tight mt-1">Activate your Genie</h3>
                <p className="text-sm text-white/85 leading-snug mt-1.5">
                  Your ready-to-ship toolkit — QR codes, a shareable link, posters and an email
                  signature, all branded and done for you.
                </p>
              </div>
            </div>

            {/* What's inside */}
            <div className="relative mt-5 flex flex-wrap gap-2">
              {["QR codes", "Shareable link", "Posters", "Email signature"].map((chip) => (
                <span
                  key={chip}
                  className="px-3 py-1.5 rounded-full bg-white/15 ring-1 ring-white/20 text-xs font-semibold"
                >
                  {chip}
                </span>
              ))}
            </div>

            {/* CTA row */}
            <div className="relative mt-6 flex items-center justify-between">
              <span className="text-[15px] font-bold">Open activation pack</span>
              <span className="w-10 h-10 rounded-full bg-white/20 ring-1 ring-white/30 flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                <ChevronRight className="w-5 h-5" />
              </span>
            </div>
          </button>
        </div>

        {/* Footer escape */}
        <div className="px-6 lg:px-8 py-5 border-t border-[#F1F3F7] flex items-center justify-between gap-3 flex-shrink-0">
          <p className="text-xs text-gray-400 leading-snug">Distribution, integrations, behaviour and more.</p>
          <button
            type="button"
            onClick={onViewAll}
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            View all features
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* ─── In-place editors (each owns its own save) — layered above this overlay ─── */}
      <KnowledgeDrawer genie={genie} isOpen={editor === "knowledge"} onClose={() => setEditor(null)} />
      <VoiceModal genie={genie} isOpen={editor === "voice"} onClose={() => setEditor(null)} />
      <BrandingModal
        key={`${genie.id}-${editor === "brand"}`}
        genie={genie}
        isOpen={editor === "brand"}
        onClose={() => setEditor(null)}
      />
      <PhoneNumberModal genie={genie} isOpen={editor === "phone"} onClose={() => setEditor(null)} />
    </div>
  );
};

export default GenieSetupImmersive;
